import type { AgentTurn, VoiceClientMessage, VoiceEndReason, VoiceServerMessage } from '@integrate/contracts';
import {
  PlaybackQueue,
  browserRealtimeAudio,
  pcm16FromBase64,
  type RealtimeAudioEnvironment,
  type AudioSource,
} from './realtime-audio.js';

/*
  One spoken conversation, from this browser's side.

  WHAT THIS OWNS is the ORDER of things: microphone before socket, socket before audio leaves, and
  everything released on the way out however the way out was reached. None of that is React's job and
  none of it is a component's — a hook that did this would re-run on renders nobody thought about,
  during a session that is metered by the minute.

  THE SOCKET IS INJECTED, like every other outward-facing thing in this project (`HttpPost`,
  `BinaryPost`, `CaptureEnvironment`, `UpstreamSocket`) and for the same reason: happy-dom has no
  `WebSocket` worth driving and no `AudioContext` at all, so a session that built its own could only
  be exercised by a human with a microphone. That is exactly how, in this project's own words, "the
  four unreachable-surface bugs got shipped".
*/

/** The near socket, narrowed to what a conversation needs. */
export interface VoiceSocket {
  send(data: string): void;
  close(): void;
  onMessage(handler: (raw: unknown) => void): void;
  /**
   * The code and reason are carried through rather than dropped, and they are the only thing that
   * distinguishes two very different endings.
   *
   * Every one of the server's refusals happens AFTER the upgrade completes — the route takes no
   * preHandler, so the socket is accepted and then closed with a status. That is the same fact
   * `store.ts` records about the editing socket, where ignoring it cost a reconnect loop that could
   * not back off. Here it would cost something quieter and worse: a refusal reported to the person as
   * a network fault, which invites them to try again forever.
   */
  onClose(handler: (code: number, reason: string) => void): void;
  onError(handler: () => void): void;
}

export type OpenVoiceSocket = (url: string) => Promise<VoiceSocket>;

/**
 * Why a session ended, as the person experiences it.
 *
 * TWO OF THE CLIENT'S OWN, added to the server's five. A socket that stopped without a `closing`
 * frame is not any of the reasons the server can give, and reporting it as one would be a guess
 * presented as a fact — "your ten minutes are up" when the wifi dropped. `refused` is the other:
 * a close code without a frame means the route turned the session down, which is a sentence to read
 * rather than a condition to retry.
 */
export type ConversationEnd = VoiceEndReason | 'network' | 'refused';

export interface ConversationHandlers {
  /** A chunk of what the model is saying, as it says it. */
  onSaid(delta: string): void;
  /** What the person said, once their turn was transcribed. */
  onHeard(text: string): void;
  /** They started talking over the answer. The UI usually clears the partial line. */
  onInterrupted(): void;
  /** A completed turn, carrying whatever proposal it raised. */
  onTurn(turn: AgentTurn): void;
  /** It is over. Called exactly once. `detail` is the server's own sentence, when it gave one. */
  onEnded(reason: ConversationEnd, detail?: string): void;
}

/**
 * The address, built the way the store builds its own.
 *
 * Same-origin and scheme-matched rather than configured: the relay is OUR server, which is the whole
 * security design — the browser never holds the Azure key and never learns the Azure host.
 */
export function voiceSocketUrl(projectId: string, scenarioId: string | null): string {
  const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const query = scenarioId ? `?scenarioId=${encodeURIComponent(scenarioId)}` : '';
  return `${scheme}://${window.location.host}/ws/projects/${encodeURIComponent(projectId)}/voice${query}`;
}

export class RealtimeConversation {
  private source: AudioSource | null = null;
  private playback: (ReturnType<RealtimeAudioEnvironment['openPlayback']>) | null = null;
  private queue: PlaybackQueue | null = null;
  private socket: VoiceSocket | null = null;
  private ended = false;
  private muted = false;
  /** What the server said on the way out, if it got the chance. See `ConversationEnd`. */
  private closingReason: VoiceEndReason | null = null;
  /**
   * Whether the ending was ours.
   *
   * Remembered rather than inferred from the close code, because 1000 is what the server sends for
   * every one of its own endings too — a ceiling, an idle close and a goodbye are all "normal". This
   * is the only thing that distinguishes the ending somebody chose from the four they did not, and
   * without it a deliberate End with a lost `closing` frame would report a dropped connection.
   */
  private saidBye = false;

  readonly startedAt = Date.now();

  private constructor(private readonly handlers: ConversationHandlers) {}

  /**
   * Start one, or throw before anything is dialled.
   *
   * THE MICROPHONE COMES FIRST, AND THAT ORDERING IS THE POINT OF THIS METHOD.
   *
   * A socket opened first starts the server's clock: a ten-minute ceiling and — far sooner — a
   * ninety-second idle close. A browser permission prompt is a dialog somebody has to find, read and
   * answer, and on a first use it can easily outlast ninety seconds of silence. Opening the socket
   * before the microphone therefore risks closing the session under somebody who is doing exactly
   * what they were asked to do, and it bills for the wait.
   *
   * It also means a REFUSED microphone never dials at all. The cheapest failure is the one that costs
   * nothing, and the person who clicks "Block" should not appear in an invoice.
   */
  static async open(options: {
    projectId: string;
    scenarioId: string | null;
    handlers: ConversationHandlers;
    audio?: RealtimeAudioEnvironment;
    openSocket?: OpenVoiceSocket;
  }): Promise<RealtimeConversation> {
    const audio = options.audio ?? browserRealtimeAudio();
    const openSocket = options.openSocket ?? browserVoiceSocket;
    const conversation = new RealtimeConversation(options.handlers);

    // Frames captured before the socket exists are DROPPED rather than buffered. They are the
    // microphone warming up while the relay is still being authorised — a fraction of a second of a
    // room, and holding it to replay later would send audio recorded before the session existed.
    conversation.source = await audio.openSource((b64) => conversation.frame(b64));

    let socket: VoiceSocket;
    try {
      socket = await openSocket(voiceSocketUrl(options.projectId, options.scenarioId));
    } catch (cause) {
      // The microphone is already live at this point, and a failed dial must not leave it that way:
      // the browser's recording indicator staying lit after an error is the application appearing to
      // listen to somebody it just told it could not.
      conversation.source.stop();
      conversation.source = null;
      throw cause;
    }

    conversation.socket = socket;
    conversation.playback = audio.openPlayback();
    conversation.queue = new PlaybackQueue(conversation.playback);

    socket.onMessage((raw) => conversation.receive(raw));
    socket.onClose((code, reason) => conversation.closed(code, reason));
    socket.onError(() => conversation.finish('network'));
    return conversation;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  get elapsedMs(): number {
    return Date.now() - this.startedAt;
  }

  /**
   * Mute, in BOTH places, and neither is redundant.
   *
   * The frame is what keeps the session alive — a client that merely went quiet would be
   * indistinguishable from one whose network had gone, and the server's idle timer would close a
   * conversation somebody deliberately paused. Dropping the frames locally is what makes the mute
   * mean what the word means: while it is on, the room's audio does not leave this browser at all.
   * A mute that relayed everything and trusted the far end to ignore it would be a label, not a
   * control.
   */
  setMuted(on: boolean): void {
    if (this.ended || this.muted === on) return;
    this.muted = on;
    this.send({ t: 'mute', on });
    // Anything already scheduled keeps playing on unmute-to-mute — muting the microphone is not
    // muting the speaker, and cutting the answer off mid-word would read as a bug.
  }

  /** Ordinary end, from the person. */
  end(): void {
    if (this.ended) return;
    this.saidBye = true;
    this.send({ t: 'bye' });
    this.socket?.close();
    // Not `finish()` here: the socket's own close event calls it, and it is the one path that also
    // runs when the ending was not ours to begin with.
  }

  /** Release everything, immediately, without waiting for a handshake. For an unmounting component. */
  abandon(): void {
    this.socket?.close();
    this.finish('network');
  }

  private frame(b64: string): void {
    if (this.ended || this.muted) return;
    this.send({ t: 'audio', b64 });
  }

  private send(message: VoiceClientMessage): void {
    try {
      this.socket?.send(JSON.stringify(message));
    } catch {
      // The socket is gone; `onClose` is already on its way. A throw here would surface inside an
      // audio callback, where nothing can act on it.
    }
  }

  private receive(raw: unknown): void {
    if (this.ended) return;
    const message = raw as VoiceServerMessage;
    switch (message?.t) {
      case 'audio':
        this.queue?.play(pcm16FromBase64(message.b64));
        return;
      case 'said':
        this.handlers.onSaid(message.delta);
        return;
      case 'heard':
        this.handlers.onHeard(message.text);
        return;
      case 'interrupted':
        /*
          THE PLAYBACK IS CLEARED, NOT PAUSED, and this line is what barge-in IS on this side. Audio
          already handed to the output has left this code's control, so dropping the queue is not
          enough — the point of interrupting a wrong answer is that it stops now.
        */
        this.queue?.clear();
        this.handlers.onInterrupted();
        return;
      case 'turn':
        this.handlers.onTurn(message.turn);
        return;
      case 'closing':
        // Recorded rather than acted on: the socket closes a moment later and `finish` is the single
        // exit. Acting here would end the session twice on the ordinary path.
        this.closingReason = message.reason;
        return;
      default:
        // An unknown frame is a newer server, not a broken one.
        return;
    }
  }

  /**
   * One way out, whatever caused it.
   *
   * THE MICROPHONE IS RELEASED ON EVERY PATH THROUGH HERE, which is the only property of this class
   * worth being certain about. A stream left running keeps the browser's recording indicator lit
   * after the conversation is visibly over, and people reasonably read that as the application still
   * listening to them.
   */
  private finish(reason: ConversationEnd, detail?: string): void {
    if (this.ended) return;
    this.ended = true;
    this.source?.stop();
    this.source = null;
    this.queue?.clear();
    this.queue = null;
    this.playback?.close();
    this.playback = null;
    this.handlers.onEnded(reason, detail);
  }

  /**
   * The socket closed. Which of three things that was depends on what preceded it.
   *
   * A `closing` frame is the server saying why in its own vocabulary, and it wins. Without one, a
   * close code in the 1008/1011 range is a REFUSAL — the route accepted the upgrade and then turned
   * the session down, which is what every refusal on this server looks like — and its reason string
   * is a sentence somebody can act on. Anything else is the wire.
   */
  private closed(code: number, reason: string): void {
    if (this.closingReason) {
      this.finish(this.closingReason);
      return;
    }
    if (code === 1008 || code === 1011) {
      this.finish('refused', reason || undefined);
      return;
    }
    this.finish(this.saidBye ? 'bye' : 'network');
  }
}

/**
 * The real socket. Untested, like `browserCapture` and `browserRealtimeAudio`, and for the same
 * reason: there is nothing here but wiring.
 */
async function browserVoiceSocket(url: string): Promise<VoiceSocket> {
  const socket = new WebSocket(url);
  await new Promise<void>((resolve, reject) => {
    socket.addEventListener('open', () => resolve(), { once: true });
    socket.addEventListener('error', () => reject(new Error('The assistant could not be reached.')), {
      once: true,
    });
  });
  return {
    send: (data) => socket.send(data),
    close: () => socket.close(),
    onMessage: (handler) =>
      socket.addEventListener('message', (event) => {
        try {
          handler(JSON.parse(String(event.data)));
        } catch {
          // Not JSON. `receive` would have ignored it anyway.
        }
      }),
    onClose: (handler) =>
      socket.addEventListener('close', (event) => handler(event.code, event.reason), { once: true }),
    onError: (handler) => socket.addEventListener('error', () => handler(), { once: true }),
  };
}
